import React, { useState } from "react"

import { useNavigate } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { useApplication } from "@/contexts/ApplicationContext"
import { useToast } from "@/hooks/use-toast"
import { useStartAssessment } from "@/hooks/useAssessments"
import { useOrientationQuizCloudFunctions } from "@/hooks/useOrientationQuizCloudFunctions"

const quizQuestions = [
  {
    id: 1,
    text: "Self-employed drivers are responsible for ensuring they wear appropriate safety equipment - what are these?",
    options: [
      { id: "1A", text: "Safety gloves", points: 0 },
      { id: "1B", text: "Hi-vis and Safety glasses", points: 0 },
      { id: "1C", text: "Hard hat", points: 0 },
      { id: "1D", text: "Hi-vis and Safety shoes", points: 1 },
    ],
  },
  {
    id: 2,
    text: "When manually handling a cage or parcel, what should you do to reduce the risk of injury?",
    options: [
      {
        id: "2A",
        text: "Lift quickly and keep the load away from your body",
        points: 0,
      },
      {
        id: "2B",
        text: "Bend from the waist and use your back to lift",
        points: 0,
      },
      {
        id: "2C",
        text: "Check the load, use a safe lifting technique, and keep it close to you",
        points: 1,
      },
      {
        id: "2D",
        text: "Twist your body while lifting to place the load where needed",
        points: 0,
      },
    ],
  },
  {
    id: 3,
    text: "What app is used to reach out to Driver Operations?",
    options: [
      { id: "3A", text: "Track-POD", points: 0 },
      { id: "3B", text: "WhatsApp", points: 0 },
      { id: "3C", text: "HIVED App", points: 1 },
      { id: "3D", text: "Telegram", points: 0 },
    ],
  },
  {
    id: 4,
    text: "When would you message Driver Operations through the HIVED app while out on the road?",
    options: [
      {
        id: "4A",
        text: "When you have on-road or parcel issues",
        points: 1,
      },
      { id: "4B", text: "When you have block booking issues", points: 0 },
      { id: "4C", text: "When your invoice is missing a payment", points: 0 },
      { id: "4D", text: "When you want to have a chat", points: 0 },
    ],
  },
  {
    id: 5,
    text: "What should you do once all returned parcels are scanned but on the HIVED app it still says 1 in Parcels section?",
    options: [
      { id: "5A", text: "Leave and continue on with your day", points: 0 },
      {
        id: "5B",
        text: "Tell security or PUDO operators to rescan parcels",
        points: 1,
      },
      { id: "5C", text: "Let the customer know", points: 0 },
      {
        id: "5D",
        text: "Take a picture of the parcel that wasn't marked as scanned",
        points: 0,
      },
    ],
  },
  {
    id: 6,
    text: "You delivered stop 10's parcel to stop 9 by mistake. What do you do?",
    options: [
      { id: "6A", text: "Fix it after the route to avoid delays", points: 0 },
      { id: "6B", text: "Go back to stop 9, then deliver to stop 10", points: 0 },
      { id: "6C", text: "Tell stop 10, swap parcels at stop 9", points: 0 },
      {
        id: "6D",
        text: "Inform Driver Operations, fix the error, and continue with route",
        points: 1,
      },
    ],
  },
  {
    id: 7,
    text: "When can a food or perishable parcel be left unattended?",
    options: [
      {
        id: "7A",
        text: "When you are behind schedule and need to finish the route",
        points: 0,
      },
      {
        id: "7B",
        text: "If the customer has been contacted and no neighbours are home",
        points: 1,
      },
      { id: "7C", text: "When you believe the location is safe", points: 0 },
      {
        id: "7D",
        text: "When a neighbour confirms it is safe to leave",
        points: 0,
      },
    ],
  },
  {
    id: 8,
    text: "In which scenario are you allowed to leave a parcel in a safe place, even if the customer hasn't confirmed it?",
    options: [
      {
        id: "8A",
        text: "If you are busy and want to finish the route quicker",
        points: 0,
      },
      {
        id: "8B",
        text: "Any parcel when you think it's safe, and nobody is home",
        points: 0,
      },
      {
        id: "8C",
        text: "If the neighbour is not at home, but you still need to deliver",
        points: 0,
      },
      {
        id: "8D",
        text: "If no-one is at home or picks up the phone, and there is a 'Must deliver' on the delivery app",
        points: 1,
      },
    ],
  },
  {
    id: 9,
    text: "How many 'Proof of Delivery' pictures are required when delivering to a flat in an apartment?",
    options: [
      { id: "9A", text: "0", points: 0 },
      { id: "9B", text: "1", points: 0 },
      { id: "9C", text: "2", points: 0 },
      { id: "9D", text: "3+", points: 1 },
    ],
  },
  {
    id: 10,
    text: "When taking a Proof of Delivery photo, what should you make sure of?",
    options: [
      {
        id: "10A",
        text: "Make sure the customer is visible in the photo",
        points: 0,
      },
      {
        id: "10B",
        text: "Take a photo of the customer holding the parcel",
        points: 0,
      },
      {
        id: "10C",
        text: "Make sure no people's faces are visible in the photo",
        points: 1,
      },
      {
        id: "10D",
        text: "Take a photo from inside the customer's home",
        points: 0,
      },
    ],
  },
  {
    id: 11,
    text: "You are delivering an age-restricted item but the customer looks as if they could be under 25 years old. What should you do?",
    options: [
      {
        id: "11A",
        text: "Still deliver if they say someone above the age is home",
        points: 0,
      },
      {
        id: "11B",
        text: "Ask for ID and write Date of Birth in notes",
        points: 1,
      },
      { id: "11C", text: "Mark the parcel as undelivered", points: 0 },
      {
        id: "11D",
        text: "Leave with a neighbour who looks above the age of 25",
        points: 0,
      },
    ],
  },
  {
    id: 12,
    text: "You've completed your route with 3 undelivered parcels after re-attempts. What should you do?",
    options: [
      {
        id: "12A",
        text: "Wait outside the customers home until they return",
        points: 0,
      },
      { id: "12B", text: "Leave the parcels in a safe place", points: 0 },
      {
        id: "12C",
        text: "Return the parcels to the warehouse or a nearby PUDO point",
        points: 1,
      },
      {
        id: "12D",
        text: "Go home and re-attempt them first thing the next morning",
        points: 0,
      },
    ],
  },
  {
    id: 13,
    text: "When should you return your undelivered parcels?",
    options: [
      { id: "13A", text: "The next day", points: 0 },
      { id: "13B", text: "Mid way through your route", points: 0 },
      { id: "13C", text: "By the end of the day", points: 1 },
      {
        id: "13D",
        text: "End of the week with all undelivered parcels at once",
        points: 0,
      },
    ],
  },
  {
    id: 14,
    text: "You arrive at the correct address, but the customer now lives 7 minutes away as they entered their old address. What do you do?",
    options: [
      {
        id: "14A",
        text: "Always try to attempt delivery if its within a 10 mile radius and if not then to inform Driver Operations and follow guidance",
        points: 0,
      },
      {
        id: "14B",
        text: "Tell the customer to update address via HIVED customer support for redelivery tomorrow",
        points: 1,
      },
      {
        id: "14C",
        text: "Ask customer to update via live chat; say you may return today",
        points: 0,
      },
      {
        id: "14D",
        text: "Nothing, let HIVED deal with it the next day, its not your problem",
        points: 0,
      },
    ],
  },
  {
    id: 15,
    text: "You are delivering in a block of flats, the customer let you inside the building but now they are not answering their door. What do you do?",
    options: [
      {
        id: "15A",
        text: "Leave the parcel with a neighbour or safe place/ in person concierge if available",
        points: 1,
      },
      {
        id: "15B",
        text: "Leave the parcel at the door, take photos and notes",
        points: 0,
      },
      {
        id: "15C",
        text: "Call, knock and wait until they answer. You know they're in so might as well deliver directly to the customer",
        points: 0,
      },
      {
        id: "15D",
        text: "Mark as undelivered and return the parcel to be reattempted the next day",
        points: 0,
      },
    ],
  },
]

const OrientationQuiz: React.FC = () => {
  const [started, setStarted] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState(1)
  const { application } = useApplication()
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const { toast } = useToast()
  const navigate = useNavigate()

  // Assessment mutations
  const { mutate: startAssessment } = useStartAssessment()
  const { submitOrientationQuiz } = useOrientationQuizCloudFunctions()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleStartQuiz = () => {
    setStarted(true)

    // Record the assessment start time if we have an application ID
    if (application?.id) {
      startAssessment({
        applicationId: application.id,
        type: "orientation-quiz",
      })
    }
  }

  const handleAnswerChange = (questionId: number, optionId: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: optionId,
    }))
  }

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length) {
      setCurrentQuestion(currentQuestion + 1)
    }
  }

  const handlePrevious = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Format answers for submission
      const formattedAnswers: Record<string, string> = {}
      Object.entries(answers).forEach(([questionId, answerId]) => {
        formattedAnswers[questionId] = answerId
      })

      // Submit to cloud function - score calculation is now done on the backend
      const result = await submitOrientationQuiz.mutateAsync({
        applicationId: application.applicationId,
        resultBreakdown: {
          answers: formattedAnswers,
        },
      })

      if (!result.success) {
        toast({
          title: "Submission Error",
          description: result.message || "Failed to submit quiz",
          variant: "destructive",
        })
        return
      }

      // Handle success
      if (result.data?.passed) {
        toast({
          title: "Quiz Passed",
          description: "You have successfully passed the orientation quiz!",
        })

        // Navigate to the next stage if provided
        if (result.data.nextStage) {
          navigate(`/${result.data.nextStage}`)
        } else {
          // Default navigation if no next stage is provided
          navigate("/personal-details")
        }
      } else {
        toast({
          title: "Quiz Failed",
          description: "Unfortunately, you did not pass the orientation quiz. Please try again.",
          variant: "destructive",
        })

        // Navigate to failure page if provided, otherwise stay on quiz
        if (result.data?.nextStage) {
          navigate(`/${result.data.nextStage}`)
        }
      }
    } catch (error) {
      console.error("Error submitting quiz:", error)
      toast({
        title: "Submission Error",
        description: "There was an error submitting your quiz. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }
  console.log("OrientationQuiz", started)
  if (!started) {
    return (
      <Card className="p-6 shadow-sm">
        <h2 className="mb-4 text-xl font-semibold">Orientation Quiz</h2>
        <div className="prose mb-6 max-w-none">
          <p className="mb-4 text-gray-700">
            This quiz will test your understanding of the key safety procedures, delivery protocols,
            and operational guidelines covered in your orientation session.
          </p>
          <p className="mb-4 text-gray-700">
            Please answer all questions to the best of your ability. Your answers will be evaluated
            based on the job requirements to determine if you pass.
          </p>
        </div>
        <Button
          onClick={handleStartQuiz}
          className="w-full bg-honeycomb text-white hover:bg-amber-500 md:w-auto"
        >
          Start Quiz
        </Button>
      </Card>
    )
  }

  const activeQuestion = quizQuestions.find((q) => q.id === currentQuestion)
  if (!activeQuestion) return null

  const progress = (currentQuestion / quizQuestions.length) * 100

  return (
    <Card className="p-5 shadow-sm">
      <div className="mb-4">
        <div className="h-2 w-full rounded-full bg-gray-200">
          <div
            className="h-2 rounded-full bg-honeycomb transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="mt-1 text-right text-sm text-gray-500">
          Question {currentQuestion} of {quizQuestions.length}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div key={activeQuestion.id} className="space-y-4">
          <div className="mb-4">
            <h3 className="text-lg font-medium">{activeQuestion.text}</h3>
          </div>

          <RadioGroup
            value={answers[activeQuestion.id] || ""}
            onValueChange={(value) => handleAnswerChange(activeQuestion.id, value)}
            className="space-y-3"
          >
            {activeQuestion.options.map((option) => (
              <div
                key={option.id}
                className="flex items-center space-x-3 rounded-md border border-gray-200 p-3"
              >
                <RadioGroupItem value={option.id} id={option.id} />
                <Label htmlFor={option.id} className="flex-1 font-normal text-gray-700">
                  {option.text}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="flex justify-between pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handlePrevious}
            disabled={currentQuestion === 1}
          >
            Previous
          </Button>

          {currentQuestion < quizQuestions.length ? (
            <Button
              type="button"
              onClick={handleNext}
              className="bg-honeycomb text-white hover:bg-amber-500"
              disabled={!answers[currentQuestion]}
            >
              Next
            </Button>
          ) : (
            <Button
              type="submit"
              className="bg-honeycomb text-white hover:bg-amber-500"
              disabled={!quizQuestions.every((q) => answers[q.id]) || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                  Submitting...
                </>
              ) : (
                "Submit Quiz"
              )}
            </Button>
          )}
        </div>
      </form>
    </Card>
  )
}

export default OrientationQuiz
